import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Wifi } from 'lucide-react';

interface ChangelogEntry {
  version: string;
  date: string;
  time: string;
  type: 'Major' | 'Minor';
  description: string;
  changes: {
    [key: string]: string[];
  };
  componentsUpdated?: string[];
}

export function VersionHistory() {
  const [selectedCategories, setSelectedCategories] = useState<{[key: string]: string}>({});
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  
  // Update current time every second for real-time accuracy
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get real-time device date and time with full locale support
  const getCurrentDateTime = (date: Date = currentDateTime) => {
    const dateString = date.toLocaleDateString(undefined, { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const timeString = date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    return { 
      date: dateString, 
      time: timeString,
      timezone,
      full: `${dateString} at ${timeString} (${timezone})`
    };
  };

  // Helper function to get relative dates with device settings
  const getRelativeDate = (daysAgo: number) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    
    const dateString = date.toLocaleDateString(undefined, { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    const timeString = date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    return { date: dateString, time: timeString };
  };

  // Get all relative dates
  const liveDateTime = getCurrentDateTime();
  const oneDayAgo = getRelativeDate(1);
  const twoDaysAgo = getRelativeDate(2);
  const threeDaysAgo = getRelativeDate(3);
  const fourDaysAgo = getRelativeDate(4);
  const fiveDaysAgo = getRelativeDate(5);
  const sixDaysAgo = getRelativeDate(6);
  
  const changelog: ChangelogEntry[] = [
    {
      version: "V1.4.5",
      date: liveDateTime.date,
      time: liveDateTime.time,
      type: "Minor",
      description: "Streamlined Tap In/Out interface by removing redundant sections",
      componentsUpdated: [
        "/components/CheckIn.tsx"
      ],
      changes: {
        "Interface Cleanup": [
          "Removed empty Card component that was cluttering the CheckIn interface",
          "Eliminated 'Recent Entries' section showing today's workspace entries for cleaner layout",
          "Removed 'Daily Summary' section displaying daily usage statistics and analytics",
          "Streamlined Tap In/Out interface by consolidating redundant display elements",
          "Maintained core functionality while reducing visual complexity",
          "Enhanced focus on primary features: Register Entry, Statistics, and Active Sessions"
        ],
        "User Experience Enhancement": [
          "Simplified workflow by removing duplicate information displays",
          "Improved visual hierarchy with fewer competing interface elements",
          "Enhanced focus on essential Tap In/Out functionality",
          "Reduced information overload while preserving critical features",
          "Maintained all core functionality: entry registration, QR scanning, and session management",
          "Preserved Usage History section for complete workspace usage records"
        ],
        "Code Optimization": [
          "Cleaned up unused Card components and empty elements",
          "Removed redundant data display logic for recent entries",
          "Eliminated duplicate daily statistics rendering code",
          "Streamlined component structure for better maintainability",
          "Reduced component complexity while preserving functionality",
          "Optimized rendering performance by removing unnecessary sections"
        ],
        "Feature Consolidation": [
          "Maintained essential statistics in Today's Statistics section",
          "Preserved Active Client Sessions table for current workspace users",
          "Kept comprehensive Usage History for complete usage tracking",
          "Maintained all dialog functionality for check-in/check-out confirmations",
          "Preserved QR scanning and manual search capabilities",
          "Consolidated interface elements for more efficient workspace management"
        ]
      }
    },
    {
      version: "V1.4.4",
      date: oneDayAgo.date,
      time: oneDayAgo.time,
      type: "Minor",
      description: "Removed live indicators while maintaining real-time device logging functionality",
      componentsUpdated: [
        "/components/VersionHistory.tsx",
        "/components/VersionDisplay.tsx"
      ],
      changes: {
        "User Interface Cleanup": [
          "Removed visual live indicators (LIVE badge with pulsing animation) for cleaner interface",
          "Eliminated clock icons and timezone display indicators from current version",
          "Removed bottom status indicator about real-time logging activity",
          "Simplified version display without distracting live status badges",
          "Maintained clean, professional appearance without animated elements",
          "Streamlined version history interface for better focus on content"
        ],
        "Real-Time Functionality Preserved": [
          "Maintained real-time timestamp updates that refresh every second for current version",
          "Preserved device date/time integration with full locale and timezone support",
          "Kept automatic time synchronization with user's device settings",
          "Continued real-time clock functionality running in background",
          "Maintained all timezone detection and formatting capabilities",
          "Preserved performance optimization with proper timer cleanup"
        ],
        "Code Optimization": [
          "Removed isLive property and related conditional rendering logic",
          "Eliminated live indicator styling and animation classes",
          "Simplified component structure by removing visual indicator components",
          "Maintained core real-time functionality without UI complexity",
          "Reduced component rendering overhead by removing animated elements",
          "Streamlined conditional logic for version display"
        ],
        "Version History Enhancement": [
          "Improved focus on version content without visual distractions",
          "Enhanced readability by removing animated UI elements",
          "Maintained complete version history with all technical details",
          "Preserved categorized changelog structure and component tracking",
          "Kept comprehensive change documentation and descriptions",
          "Enhanced user experience with cleaner, distraction-free interface"
        ]
      }
    },
    {
      version: "V1.4.3",
      date: twoDaysAgo.date,
      time: twoDaysAgo.time,
      type: "Minor",
      description: "Enhanced Version History with real-time device logging and live timestamps",
      componentsUpdated: [
        "/components/VersionHistory.tsx",
        "/components/VersionDisplay.tsx"
      ],
      changes: {
        "Real-Time Device Integration": [
          "Implemented live timestamp updates that refresh every second for current version",
          "Enhanced date/time formatting to use device's complete locale and timezone settings",
          "Added real-time clock functionality that continuously shows current device time",
          "Integrated timezone detection with Intl.DateTimeFormat for accurate global support",
          "Added seconds precision to current version timestamp for real-time accuracy",
          "Implemented automatic timezone display showing user's actual location settings"
        ],
        "Live Timestamp Features": [
          "Current version timestamp now updates every second in real-time",
          "Added visual indicator (live badge) to show real-time updates for current version",
          "Implemented useEffect hook with setInterval for continuous time updates",
          "Enhanced time display with seconds for precise timestamp accuracy",
          "Added timezone information display for international user support",
          "Integrated automatic cleanup of timer intervals for performance optimization"
        ],
        "Device Accuracy Enhancements": [
          "Full locale support using browser's Intl API for international compatibility",
          "Automatic timezone detection using Intl.DateTimeFormat().resolvedOptions()",
          "Enhanced date formatting to match user's device language and region settings",
          "Improved time formatting with proper 12/24 hour support based on device preferences",
          "Added comprehensive timezone display showing actual device timezone",
          "Real-time updates ensure timestamps always reflect current device date/time"
        ],
        "User Experience Improvements": [
          "Added Clock icon and Live badge to indicate real-time functionality",
          "Enhanced visual feedback showing that timestamps are actively updating",
          "Improved timestamp accuracy with second-level precision for current version",
          "Added timezone information for global users to verify accuracy",
          "Enhanced version history display with clear live vs historical distinction",
          "Optimized performance with proper timer cleanup and efficient updates"
        ]
      }
    },
    {
      version: "V1.4.2",
      date: threeDaysAgo.date,
      time: threeDaysAgo.time,
      type: "Minor",
      description: "Simplified authentication by removing signup functionality",
      componentsUpdated: [
        "/components/Login.tsx",
        "/components/VersionDisplay.tsx",
        "/components/VersionHistory.tsx"
      ],
      changes: {
        "Authentication Simplification": [
          "Removed signup functionality from Login component for streamlined authentication",
          "Eliminated tabbed interface (Login/Signup) in favor of single login form",
          "Removed all signup-related form states, validation, and error handling",
          "Simplified component structure by removing Tabs component usage",
          "Maintained existing login functionality with username/password authentication",
          "Preserved quick login feature for demo accounts across all user roles"
        ],
        "User Interface Cleanup": [
          "Removed signup form fields (Full Name, Email, Username, Password, Confirm Password)",
          "Eliminated signup validation logic and duplicate username checking",
          "Simplified login page layout with single authentication card",
          "Removed signup-related loading states and error messages",
          "Maintained clean, professional login interface with password visibility toggle",
          "Kept quick login functionality for easy demo access"
        ],
        "Component Structure": [
          "Removed Tabs component import and usage from Login component",
          "Eliminated signup-related state management (signupData, showSignupPassword, etc.)",
          "Simplified component props interface to focus only on login functionality",
          "Removed signup form submission handler and related validation logic",
          "Maintained existing login form validation and error handling",
          "Preserved role-based authentication and user type management"
        ],
        "Code Optimization": [
          "Reduced component complexity by removing unused signup code paths",
          "Eliminated unnecessary state variables and form handling logic",
          "Simplified component render logic without tab-based conditional rendering",
          "Maintained proper TypeScript typing while removing signup interfaces",
          "Preserved security and authentication flow for existing login functionality",
          "Optimized component performance by reducing state management overhead"
        ]
      }
    },
    {
      version: "V1.4.1",
      date: fourDaysAgo.date,
      time: fourDaysAgo.time,
      type: "Minor",
      description: "Updated Space Manager navigation and implemented dynamic version history",
      componentsUpdated: [
        "/App.tsx",
        "/components/VersionHistory.tsx",
        "/components/VersionDisplay.tsx"
      ],
      changes: {
        "Navigation Access Control": [
          "Restricted Space Manager navigation to only essential features",
          "Space Manager now has access to: Tap In/Out, Client Management, Profile & Settings, Sign Out",
          "Removed Dashboard, Subscriptions, Packages, and Reports from Space Manager navigation",
          "System Admin retains full navigation access to all system features",
          "Enhanced role-based access control for different staff levels",
          "Streamlined Space Manager workflow to focus on core operational tasks"
        ],
        "Version History Enhancement": [
          "Implemented dynamic date and time using device local settings",
          "Version history now shows current device date and time automatically",
          "Replaced hardcoded dates with JavaScript Date objects for accuracy",
          "Added proper locale-aware date and time formatting",
          "Enhanced timestamp precision with device timezone support",
          "Improved version tracking with real-time date and time updates"
        ],
        "User Experience Improvements": [
          "Simplified Space Manager interface for focused operational workflow",
          "Reduced navigation complexity for Space Manager role",
          "Maintained full functionality while improving role-appropriate access",
          "Enhanced role differentiation between System Admin and Space Manager",
          "Improved user interface clarity with role-specific navigation",
          "Streamlined workflow for different user permission levels"
        ],
        "Technical Enhancements": [
          "Added dynamic date/time generation using browser locale settings",
          "Implemented relative date calculation for historical entries",
          "Enhanced TypeScript date handling with proper formatting",
          "Improved component structure for role-based navigation rendering",
          "Added proper locale-aware formatting for international users",
          "Enhanced system architecture for role-based feature access"
        ]
      }
    },
    {
      version: "V1.4.0",
      date: fiveDaysAgo.date,
      time: fiveDaysAgo.time,
      type: "Major",
      description: "Added Member user type with signup functionality and package viewing capabilities",
      componentsUpdated: [
        "/components/Login.tsx",
        "/components/MemberDashboard.tsx",
        "/components/UserProfile.tsx",
        "/App.tsx",
        "/components/VersionDisplay.tsx",
        "/components/VersionHistory.tsx"
      ],
      changes: {
        "Authentication System Enhancement": [
          "Added comprehensive signup functionality for new Members with full form validation",
          "Implemented tabbed interface in Login component with Login and Sign Up options",
          "Added form validation for signup including password confirmation and strength requirements",
          "Enhanced quick login demo accounts to include Member role for testing",
          "Added proper error handling for duplicate usernames and invalid inputs",
          "Implemented loading states and user feedback during account creation process"
        ],
        "New Member User Type": [
          "Created dedicated Member role with specialized permissions and interface",
          "Added Member to authentication system with role-based access control",
          "Implemented Member-specific navigation showing only relevant features",
          "Created role-based color coding (green) and icon (User) for Member accounts",
          "Added Member permissions focused on package viewing and profile management",
          "Enhanced user profile system to handle three distinct user types"
        ],
        "Member Dashboard": [
          "Created comprehensive MemberDashboard component displaying all available packages",
          "Implemented beautiful package cards with pricing, features, and descriptions",
          "Added package type categorization (hourly, daily, package, monthly) with color coding",
          "Created detailed feature lists with icons for each package benefit",
          "Implemented popular package highlighting with special visual treatment",
          "Added savings calculations and original price comparisons for package deals"
        ],
        "Role-Based Navigation": [
          "Implemented dynamic navigation system based on user role",
          "Members see simplified navigation: Packages, Profile & Settings",
          "Staff (Admin/Manager) retain full navigation: Tap In/Out, Dashboard, Client Management, etc.",
          "Added role-based default landing pages (Members → Packages, Staff → Tap In/Out)",
          "Enhanced navigation styling with role-appropriate prominent buttons",
          "Maintained consistent user experience across different user types"
        ]
      }
    },
    {
      version: "V1.3.3",
      date: sixDaysAgo.date,
      time: sixDaysAgo.time,
      type: "Minor",
      description: "Consolidated Usage Tracking into Tap In/Out and enhanced version history",
      componentsUpdated: [
        "/components/CheckIn.tsx",
        "/App.tsx",
        "/components/VersionHistory.tsx",
        "/components/VersionDisplay.tsx"
      ],
      changes: {
        "Feature Consolidation": [
          "Moved all Usage Tracking functionality into Tap In/Out page for unified workflow",
          "Added Manual Usage Logging section to Tap In/Out with full Log Hours functionality",
          "Integrated Daily Summary statistics directly into Tap In/Out interface",
          "Enhanced Tap In/Out with complete usage history and analytics",
          "Consolidated Today's Statistics to include revenue and client count from usage tracking",
          "Removed redundant Usage Tracking page from navigation menu"
        ],
        "Tap In/Out Enhancements": [
          "Added comprehensive Manual Usage Logging dialog with client selection and time input",
          "Integrated usage entry calculation with automatic cost and hours computation",
          "Enhanced statistics cards to show Today's Entries, Active Sessions, Hours Today, and Revenue Today",
          "Added complete Usage History table with package type color coding",
          "Integrated Daily Summary table showing date, total hours, unique clients, and revenue",
          "Enhanced existing interface with manual logging capabilities for special cases"
        ],
        "User Interface Improvements": [
          "Streamlined navigation by removing duplicate Usage Tracking page",
          "Enhanced Tap In/Out as the central hub for all entry and usage management",
          "Improved workflow efficiency with all related features in one location",
          "Added better visual organization with clear sections for different functionalities",
          "Enhanced statistics display with more comprehensive metrics",
          "Improved user experience with unified feature access"
        ],
        "Data Management": [
          "Consolidated usage data management into single component",
          "Enhanced data flow between manual logging and automatic tracking",
          "Improved data consistency across different usage recording methods",
          "Added better error handling for usage logging operations",
          "Enhanced data validation for manual time entry",
          "Improved state management for usage tracking features"
        ]
      }
    }
  ];

  const getSelectedCategory = (version: string) => {
    return selectedCategories[version] || Object.keys(changelog.find(entry => entry.version === version)?.changes || {})[0];
  };

  const setSelectedCategory = (version: string, category: string) => {
    setSelectedCategories(prev => ({
      ...prev,
      [version]: category
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1>Version History</h1>
        <p className="text-muted-foreground">
          Track all changes and updates to the Coworking Management System with real-time device logging
        </p>
        
        {/* Real-time Device Info */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
          <Wifi className="h-4 w-4 text-green-500" />
          <span>Live Device Time:</span>
          <span className="font-mono">{liveDateTime.full}</span>
        </div>
      </div>

      {/* Version Cards */}
      <div>
        {changelog.map((entry) => {
          const selectedCategory = getSelectedCategory(entry.version);
          return (
            <Card key={entry.version} className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle>{entry.version}</CardTitle>
                    <Badge 
                      variant={entry.type === 'Major' ? 'default' : 'secondary'}
                      className={entry.type === 'Major' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}
                    >
                      {entry.type}
                    </Badge>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <div>{entry.date}</div>
                    <div className="font-mono">{entry.time}</div>
                  </div>
                </div>
                <CardDescription>{entry.description}</CardDescription>
                
                {/* Components Updated Section */}
                {entry.componentsUpdated && (
                  <div className="mt-3 p-3 bg-muted rounded-md">
                    <p className="text-sm font-medium mb-2">Components Updated:</p>
                    <div className="flex flex-wrap gap-1">
                      {entry.componentsUpdated.map((component, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {component}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <Select 
                    value={selectedCategory} 
                    onValueChange={(value) => setSelectedCategory(entry.version, value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(entry.changes).map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="space-y-2">
                    {entry.changes[selectedCategory]?.map((change, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                        <p className="text-sm">{change}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <Separator className="my-6" />
      
      <div className="text-center text-sm text-muted-foreground space-y-2">
        <p><strong>Version Guidelines:</strong></p>
        <p>Major Changes (V1.1): Restructure and adding of pages</p>
        <p>Minor Changes (V1.0.1): Changing colors, fonts, styling and text changes</p>
      </div>
    </div>
  );
}